import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const ROLE = { coordinator: ["Supervisor", "bg-primary/10 text-primary"], worker: ["Worker", "bg-secondary text-foreground"], owner: ["Owner", "bg-amber-500/15 text-amber-700"] };

const PasswordDialog = ({ person, onClose }) => {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setShow(false); }, [person]);
  const save = async () => {
    setBusy(true);
    try { await api.ownerSetPassword(person.id, pw); toast.success(`New password set for ${person.email}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not set the password"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-sm" data-testid="owner-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Set a new password</DialogTitle>
          <DialogDescription>For {person?.name || person?.email} ({person?.email}). They&apos;re signed out everywhere and use this password next time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="own-pw">New password</Label>
          <div className="relative">
            <Input id="own-pw" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} className="pr-11" placeholder="At least 6 characters" data-testid="owner-password-input" />
            <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-muted-foreground hover:text-foreground">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy || pw.length < 6} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="owner-password-save">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Set password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PeoplePanel = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [person, setPerson] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.ownerUsers(q.trim()).then(setRows).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-5" data-testid="owner-people">
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search anyone by name or email" className="pl-9" data-testid="owner-people-search" />
      </div>
      {rows === null ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : rows.length === 0 ? <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground">Nobody matches.</div>
          : (
            <div className="bg-card border border-border rounded-xl divide-y divide-border">
              {rows.map((u) => {
                const [label, cls] = ROLE[u.role] || ROLE.worker;
                return (
                  <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`owner-person-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}{u.companies.length ? ` · ${u.companies.join(", ")}` : ""}{u.depot_name ? ` · ${u.depot_name}` : ""}</p>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
                    <Button variant="outline" size="sm" onClick={() => setPerson(u)} className="rounded-full" data-testid={`owner-person-password-${u.id}`}>
                      <KeyRound className="w-4 h-4 mr-1.5" /> Set password
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
      <PasswordDialog person={person} onClose={() => setPerson(null)} />
    </div>
  );
};
